import { FilterContext, RequestCard, Spinner } from '@/components';
import { useGetRequestsInfinite } from '@/modules/Requests/queries';
import { DashboardColumnStatus, TypologyValue } from '@/shared';
import { convertDashboardStatusIntoStatus } from '@/utils';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { twJoin } from 'tailwind-merge';

interface KanbanColumnProps {
  title: string;
  extraTitle?: string;
  status: DashboardColumnStatus;
  counter?: number;
}

export const KanbanColumn = ({
  title,
  extraTitle,
  status,
  counter,
}: KanbanColumnProps) => {
  const {
    showActiveOnly,
    activeTypologies,
    filter,
    filterBy,
    sortBy,
    sortOrder,
    dateRange,
    updateNrRequestsPerColumnAndStatus,
  } = useContext(FilterContext);

  const statusMapped = useMemo(
    () =>
      convertDashboardStatusIntoStatus(status).map(
        (statusValue) => statusValue,
      ),
    [status],
  );

  const { data, fetchNextPage, isFetching } = useGetRequestsInfinite({
    onlyActiveRequests: showActiveOnly,
    activeTypologies,
    filterBy,
    filter,
    sortBy,
    sortOrder,
    dateRange,
    status: statusMapped,
  });

  const { ref: lastRowRef } = useInView({
    onChange: (inView) => {
      if (inView && !isFetching) {
        fetchNextPage?.();
      }
    },
  });

  const requests = useMemo(() => data?.pages.flat() || [], [data?.pages]);

  const filterPerTypology = useCallback(
    (typology: TypologyValue) =>
      requests?.filter((request) => request.typology === typology)?.length,
    [requests],
  );

  const {
    nrRequestsOfDevelopmentFiltered,
    nrRequestsOfDilligenceFiltered,
    nrRequestsOfDiscoveryFiltered,
  } = useMemo(
    () => ({
      nrRequestsOfDevelopmentFiltered: filterPerTypology(
        TypologyValue.Development,
      ),
      nrRequestsOfDilligenceFiltered: filterPerTypology(
        TypologyValue.Diligence,
      ),
      nrRequestsOfDiscoveryFiltered: filterPerTypology(TypologyValue.Discovery),
    }),

    [filterPerTypology],
  );

  useEffect(() => {
    updateNrRequestsPerColumnAndStatus(status, [
      {
        typology: TypologyValue.Development,
        total: nrRequestsOfDevelopmentFiltered,
      },
      {
        typology: TypologyValue.Discovery,
        total: nrRequestsOfDiscoveryFiltered,
      },
      {
        typology: TypologyValue.Diligence,
        total: nrRequestsOfDilligenceFiltered,
      },
    ]);
  }, [
    nrRequestsOfDevelopmentFiltered,
    nrRequestsOfDilligenceFiltered,
    nrRequestsOfDiscoveryFiltered,
    updateNrRequestsPerColumnAndStatus,
    status,
  ]);

  return (
    <div className="flex w-full flex-col gap-2.5 rounded bg-gray-100 p-2.5">
      <div className="flex justify-between">
        <span className="flex gap-1 text-sm font-black text-gray">
          {title}
          <span
            className={twJoin('font-bold text-gray-300', !counter && 'hidden')}
          >
            {requests?.length}
          </span>
        </span>
        <span className="text-xs font-normal italic text-gray-300">
          {extraTitle}
        </span>
      </div>
      <div className="space-y-2.5">
        {requests.map((request, index) => (
          <RequestCard
            lastRowRef={index == requests.length - 1 ? lastRowRef : null}
            key={index}
            data={request}
          />
        ))}
        {isFetching && <Spinner className="size-8" />}
      </div>
    </div>
  );
};
